import React, { FC, useEffect } from 'react';

interface SexAtBirthModalProps {
  isFr: boolean;
  onClose: () => void;
}

const SexAtBirthModal: FC<SexAtBirthModalProps> = ({ isFr, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-brand-border dark:border-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-display font-bold text-brand-text dark:text-white">
            {isFr
              ? 'Pourquoi le sexe à la naissance est important en gériatrie'
              : 'Why sex at birth matters in geriatric care'
            }
          </h2>
          <button
            onClick={onClose}
            className="p-1 text-brand-text-muted hover:text-brand-text dark:hover:text-white transition-colors"
            aria-label="Close"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-6">
          <p className="text-sm text-brand-text-light dark:text-slate-300 leading-relaxed">
            {isFr
              ? "Le sexe à la naissance est une information essentielle pour les prestataires de soins gériatriques (médecins, infirmier(ère)s, ergothérapeutes) parce qu'il reflète des différences biologiques, physiologiques et pharmacologiques qui persistent tout au long de la vie — influençant les risques de maladies, leur présentation et la réponse aux traitements."
              : 'Sex assigned at birth is critical information for geriatric healthcare providers (physicians, nurses, occupational therapists) because it informs biological, physiological, and pharmacological differences that persist throughout life — influencing disease risk, presentation, and response to treatment.'
            }
          </p>

          {/* Section 1 */}
          <div>
            <h3 className="text-sm font-bold text-brand-text dark:text-white uppercase tracking-wider mb-2">
              {isFr ? '1. Diagnostic précis' : '1. Accurate Diagnosis'}
              <span className="normal-case font-normal text-brand-text-muted ml-1">
                {isFr ? '(Médecins & Infirmier(ère)s)' : '(Physicians & Nurses)'}
              </span>
            </h3>
            <ul className="space-y-2 text-sm text-brand-text-light dark:text-slate-300 leading-relaxed">
              <li className="flex gap-2">
                <span className="text-brand-primary mt-0.5">•</span>
                <span>
                  <strong>{isFr ? 'Risques de maladies' : 'Disease risk'}:</strong>{' '}
                  {isFr
                    ? "les hommes développent les maladies cardiovasculaires plus tôt ; les femmes sont plus touchées par les maladies auto-immunes et la démence (liée au déclin hormonal post-ménopausique)."
                    : 'Men face earlier cardiovascular disease; women have higher rates of autoimmune conditions and dementia (linked to post-menopausal hormone decline).'
                  }
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-brand-primary mt-0.5">•</span>
                <span>
                  <strong>{isFr ? 'Symptômes atypiques' : 'Atypical symptoms'}:</strong>{' '}
                  {isFr
                    ? "lors d'une crise cardiaque, les femmes ressentent souvent de la fatigue, des nausées ou des douleurs à la mâchoire — et non la douleur thoracique classique observée chez les hommes. Méconnaître ces différences retarde le diagnostic."
                    : 'Women having a heart attack often report fatigue, nausea, or jaw pain — not the classic chest pain seen in men. Missing this causes diagnostic delays.'
                  }
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-brand-primary mt-0.5">•</span>
                <span>
                  <strong>{isFr ? 'Métabolisme des médicaments' : 'Medication metabolism'}:</strong>{' '}
                  {isFr
                    ? "le ratio graisse/muscle plus élevé chez les femmes modifie la façon dont les médicaments sont traités — affectant leur efficacité, le dosage requis et le risque d'effets secondaires."
                    : "Women's higher fat-to-muscle ratio changes how drugs are processed — affecting dosage, efficacy, and side-effect risk."
                  }
                </span>
              </li>
            </ul>
          </div>

          {/* Section 2 */}
          <div>
            <h3 className="text-sm font-bold text-brand-text dark:text-white uppercase tracking-wider mb-2">
              {isFr ? '2. Prévention ciblée' : '2. Targeted Prevention'}
              <span className="normal-case font-normal text-brand-text-muted ml-1">
                {isFr ? '(Infirmier(ère)s & Ergothérapeutes)' : '(Nurses & Occupational Therapists)'}
              </span>
            </h3>
            <ul className="space-y-2 text-sm text-brand-text-light dark:text-slate-300 leading-relaxed">
              <li className="flex gap-2">
                <span className="text-brand-primary mt-0.5">•</span>
                <span>
                  <strong>{isFr ? 'Ostéoporose & fractures' : 'Osteoporosis & fractures'}:</strong>{' '}
                  {isFr
                    ? "les femmes perdent leur densité osseuse plus rapidement après la ménopause. Une femme de plus de 70 ans a 5 fois plus de risque de fracture de la hanche qu'un homme du même âge. Après 80 ans, une fracture de la hanche comporte un risque de mortalité d'environ 22 % en un an."
                    : 'Women lose bone density faster after menopause. A woman over 70 is 5x more likely to fracture a hip than a man of the same age. After 80, a hip fracture carries ~22% one-year mortality risk.'
                  }
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-brand-primary mt-0.5">•</span>
                <span>
                  <strong>{isFr ? 'Dépistages' : 'Screenings'}:</strong>{' '}
                  {isFr
                    ? "le sexe à la naissance détermine quels dépistages préventifs sont nécessaires (ex. : densitométrie osseuse, suivi prostatique), indépendamment de l'identité de genre."
                    : 'Sex at birth determines which preventive screenings are needed (e.g., bone density scan, prostate monitoring) regardless of gender identity.'
                  }
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-brand-primary mt-0.5">•</span>
                <span>
                  <strong>{isFr ? 'Réadaptation' : 'Rehabilitation'}:</strong>{' '}
                  {isFr
                    ? "la recherche montre que le sexe biologique influence la vitesse de récupération après une hospitalisation — permettant aux ergothérapeutes d'adapter les protocoles d'exercice."
                    : 'Research shows biological sex influences recovery speed after hospitalization — allowing O.T.s to calibrate exercise protocols accordingly.'
                  }
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-brand-primary mt-0.5">•</span>
                <span>
                  <strong>{isFr ? 'Déclin immunitaire' : 'Immune decline'}:</strong>{' '}
                  {isFr
                    ? "les hommes connaissent un vieillissement immunitaire plus précoce (immunosénescence), nécessitant des stratégies vaccinales différentes de celles des femmes."
                    : 'Men experience earlier immune aging (immunosenescence), requiring different vaccination strategies than women.'
                  }
                </span>
              </li>
            </ul>
          </div>

          {/* Section 3 */}
          <div>
            <h3 className="text-sm font-bold text-brand-text dark:text-white uppercase tracking-wider mb-2">
              {isFr ? '3. Personnes transgenres et de genres divers' : '3. Transgender & Gender-Diverse Older Adults'}
            </h3>
            <p className="text-sm text-brand-text-light dark:text-slate-300 leading-relaxed">
              {isFr
                ? "Pour les personnes âgées transgenres, le sexe à la naissance reste cliniquement pertinent pour les risques liés aux organes (ex. : dépistage cervical pour les hommes trans) et pour la gestion des effets à long terme des thérapies hormonales d'affirmation de genre. AidFone distingue le sexe à la naissance (biologique) de l'identité de genre (sociale/psychologique) — les deux comptent, mais pour des raisons différentes."
                : "For transgender older adults, sex at birth remains clinically relevant for organ-specific risks (e.g., cervical screening for trans men) and for managing the long-term effects of gender-affirming hormone therapies. AidFone distinguishes sex at birth (biological) from gender identity (social/psychological) — both matter, for different reasons."
              }
            </p>
          </div>

          {/* How AidFone uses this */}
          <div className="bg-brand-bg-warm dark:bg-slate-700/50 rounded-xl p-4">
            <h3 className="text-sm font-bold text-brand-text dark:text-white mb-2">
              {isFr ? 'Comment AidFone utilise cette information' : 'How AidFone uses this information'}
            </h3>
            <p className="text-sm text-brand-text-light dark:text-slate-300 leading-relaxed">
              {isFr
                /* Trailing sentence removed for launch: it promised AidFone's
                   "future Fall Detection alerts" / "future fonctionnalité de
                   Détection des chutes", a cut feature. Both sentences were
                   deleted whole, so no new French was written. The clinical
                   "fall prevention" / "prévention des chutes" wording is kept —
                   that describes how the care team uses the data, not an
                   AidFone product feature. */
                ? "Avec votre consentement, cette donnée est partagée avec l'équipe soignante de votre proche pour personnaliser la prévention des chutes, les calendriers de dépistage et les priorités d'intervention d'urgence."
                : "With your consent, this data is shared with your loved one's care team to personalize fall prevention, screening schedules, and emergency response."
              }
            </p>
          </div>

          {/* Sources */}
          <p className="text-xs text-brand-text-muted dark:text-slate-500">
            <strong>Sources:</strong> National Institutes of Health (NIH), PubMed Central (PMC), Bone Health & Osteoporosis Foundation, NIAMS.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SexAtBirthModal;
